import React, { useRef } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  Autocomplete,
  Typography,
  Divider,
  IconButton,
  InputAdornment,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CButton from "../../../basecomp/components/CButton";
import SingleAssetVM, {
  FIELD_LIMITS,
  ASSET_STATUS_OPTIONS,
  WARRANTY_STATUS_OPTIONS,
  WIRED_OPTIONS,
  todayStr,
  toIso,
  isoToDisplay,
} from "../viewmodel/SingleAssetVM";

// Auto-add the slashes while typing: "15102026" -> "15/10/2026"
const formatDateTyping = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

// Date box that always shows DD/MM/YYYY (on every computer),
// with a calendar button that opens the normal date picker
const DateInput = ({ label, required, value, onChange, onBlur, error, helperText, disabled, min, max }) => {
  const pickerRef = useRef(null);

  const openPicker = () => {
    const el = pickerRef.current;
    if (!el) return;
    try {
      el.showPicker(); // modern browsers
    } catch (e) {
      el.click(); // older browsers
    }
  };

  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      required={required}
      placeholder="DD/MM/YYYY"
      value={value}
      onChange={(e) => onChange(formatDateTyping(e.target.value))}
      onBlur={onBlur}
      InputLabelProps={{ shrink: true }}
      inputProps={{ inputMode: "numeric", maxLength: 10 }}
      error={error}
      helperText={helperText}
      disabled={disabled}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end" sx={{ position: "relative" }}>
            <IconButton size="small" onClick={openPicker} disabled={disabled} edge="end">
              <CalendarMonthIcon fontSize="small" />
            </IconButton>
            {/* hidden date picker, only used to pick a date from the calendar */}
            <input
              ref={pickerRef}
              type="date"
              tabIndex={-1}
              aria-hidden="true"
              value={toIso(value) || ""}
              min={min}
              max={max}
              onChange={(e) => {
                onChange(isoToDisplay(e.target.value));
                if (onBlur) onBlur();
              }}
              style={{
                position: "absolute",
                right: 0,
                bottom: 0,
                width: 1,
                height: 1,
                opacity: 0,
                border: 0,
                padding: 0,
              }}
            />
          </InputAdornment>
        ),
      }}
    />
  );
};

const SectionTitle = ({ title }) => (
  <Grid item xs={12} sx={{ mt: 1 }}>
    <Typography sx={{ fontWeight: 600, color: "#052660", fontSize: "0.95rem" }}>
      {title}
    </Typography>
    <Divider sx={{ mt: 0.5 }} />
  </Grid>
);

const SingleAssetForm = ({ sizeOptions = [], onRegistered }) => {
  const {
    form,
    options,
    selectedSizeId,
    saving,
    isReady,
    getError,
    handleChange,
    handleBlur,
    handleSizeChange,
    handleClear,
    handleRegister,
  } = SingleAssetVM({ onRegistered });

  // Normal text box
  const textField = (key, label, required = false) => (
    <Grid item xs={12} sm={6} md={3}>
      <TextField
        fullWidth
        size="small"
        label={label}
        required={required}
        value={form[key]}
        onChange={(e) => handleChange(key, e.target.value)}
        onBlur={() => handleBlur(key)}
        error={!!getError(key)}
        helperText={getError(key) || " "}
        inputProps={{ maxLength: FIELD_LIMITS[key] }}
        disabled={saving}
      />
    </Grid>
  );

  // Text box with suggestions from existing assets (user can also type a new value)
  const suggestField = (key, label, required = false) => (
    <Grid item xs={12} sm={6} md={3}>
      <Autocomplete
        freeSolo
        options={options[key] || []}
        // Show only suggestions that match what is typed (works on every MUI version)
        filterOptions={(list) => {
          const typed = (form[key] || "").trim().toLowerCase();
          return typed ? list.filter((o) => o.toLowerCase().includes(typed)) : list;
        }}
        value={form[key] || null}
        onChange={(e, value) => handleChange(key, value || "")}
        inputValue={form[key]}
        onInputChange={(e, value) => handleChange(key, value)}
        disabled={saving}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={label}
            required={required}
            onBlur={() => handleBlur(key)}
            error={!!getError(key)}
            helperText={getError(key) || " "}
            inputProps={{ ...params.inputProps, maxLength: FIELD_LIMITS[key] }}
          />
        )}
      />
    </Grid>
  );

  // Fixed list dropdown
  const selectField = (key, label, list, allowEmpty = true) => (
    <Grid item xs={12} sm={6} md={3}>
      <TextField
        select
        fullWidth
        size="small"
        label={label}
        value={form[key]}
        onChange={(e) => handleChange(key, e.target.value)}
        error={!!getError(key)}
        helperText={getError(key) || " "}
        disabled={saving}
        sx={{ textAlign: "left" }}
      >
        {allowEmpty && (
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
        )}
        {list.map((item) => (
          <MenuItem key={item} value={item}>
            {item}
          </MenuItem>
        ))}
      </TextField>
    </Grid>
  );

  // Date box (DD/MM/YYYY)
  const dateField = (key, label, required = false, extra = {}) => (
    <Grid item xs={12} sm={6} md={3}>
      <DateInput
        label={label}
        required={required}
        value={form[key]}
        onChange={(value) => handleChange(key, value)}
        onBlur={() => handleBlur(key)}
        error={!!getError(key)}
        helperText={getError(key) || " "}
        disabled={saving}
        min={extra.min}
        max={extra.max}
      />
    </Grid>
  );

  return (
    <Grid container spacing={2} sx={{ width: "100%", m: 0, pr: 2 }}>
      <SectionTitle title="Basic details" />

      {/* Sticker size (same list as Bulk Upload) */}
      <Grid item xs={12} sm={6} md={3}>
        <TextField
          select
          fullWidth
          size="small"
          label="Sticker Size"
          required
          value={selectedSizeId}
          onChange={(e) => handleSizeChange(e.target.value)}
          error={!!getError("selectedSizeId")}
          helperText={getError("selectedSizeId") || " "}
          disabled={saving}
          sx={{ textAlign: "left" }}
        >
          {sizeOptions.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.size}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      {suggestField("category", "Category", true)}
      {textField("laptop_desktop_serial_no", "Laptop/Desktop Serial No", true)}
      {dateField("date_purchase", "Purchase Date", true, { max: todayStr() })}
      {suggestField("brand", "Brand")}
      {suggestField("series", "Series")}
      {suggestField("sub_series", "Sub Series")}
      {suggestField("type", "Type")}

      <SectionTitle title="Hardware" />
      {textField("product_id_model_no", "Product ID / Model No")}
      {textField("machine_type_model", "Machine Type / Model")}
      {suggestField("processor", "Processor")}
      {suggestField("ram_gb", "RAM (GB)")}
      {suggestField("ssd_hdd", "SSD/HDD")}
      {suggestField("graphics", "Graphics")}
      {suggestField("operating_system", "Operating System")}
      {selectField("is_wired", "Wired / Wireless", WIRED_OPTIONS)}
      {suggestField("charger_w", "Charger (Watt)")}
      {textField("product_id_charger", "Product ID / Charger ID")}

      <SectionTitle title="Warranty & status" />
      {suggestField("vendor", "Vendor")}
      {dateField("warranty_date", "Warranty Date", false, {
        min: toIso(form.date_purchase) || undefined,
      })}
      {selectField("warranty_status", "Warranty Status", WARRANTY_STATUS_OPTIONS)}
      {selectField("asset_status", "Asset Status", ASSET_STATUS_OPTIONS, false)}

      <Grid item xs={12}>
        <Typography variant="caption" sx={{ color: "#666" }}>
          * Required. Asset ID is created automatically. To give the asset to an
          employee, register it first and then use the Assign button.
        </Typography>
      </Grid>

      <Grid
        item
        xs={12}
        sx={{ display: "flex", justifyContent: "center", gap: 2, pb: "2.5em" }}
      >
        <CButton
          cText="Clear"
          cTooltipText="Clear the form"
          cOnClick={handleClear}
          cDisabled={saving}
        />
        <CButton
          cText={saving ? "Saving..." : "Register Asset"}
          cTooltipText="Save this asset"
          cOnClick={handleRegister}
          cDisabled={saving || !isReady}
        />
      </Grid>
    </Grid>
  );
};

export default SingleAssetForm;